import { Link } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useViewport } from '../../../hooks/useViewport';
import { usePublicStats } from '../../../hooks/unified/useJobsQueries';

// Minimum display values for when real data is low/empty
const MIN_DISPLAY_VALUES = {
  active_jobs: 25,
  total_candidates: 500,
  total_placements: 150,
};

export function HeroSection() {
  const { isAuthenticated } = useAuth();
  const { isMobile, prefersReducedMotion } = useViewport();
  const { data: stats } = usePublicStats();

  // Use real values if they exceed minimums, otherwise show minimums
  const displayStats = {
    active_jobs: Math.max(stats?.active_jobs || 0, MIN_DISPLAY_VALUES.active_jobs),
    total_candidates: Math.max(stats?.total_candidates || 0, MIN_DISPLAY_VALUES.total_candidates),
    total_placements: Math.max(stats?.total_placements || 0, MIN_DISPLAY_VALUES.total_placements),
  };

  // Animation classes based on reduced motion preference
  const animateClasses = prefersReducedMotion ? '' : 'animate-pulse';
  const bounceClasses = prefersReducedMotion ? '' : 'animate-bounce';
  const hoverScaleClasses = prefersReducedMotion ? '' : 'transform hover:scale-105';
  const transitionClasses = prefersReducedMotion ? '' : 'transition-all duration-300';

  // On mobile, use calc to account for header height (56px = h-14)
  const heightClass = isMobile 
    ? 'min-h-[calc(100vh-56px)]' 
    : 'min-h-[90vh]';

  return (
    <section className={`relative bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900 text-white overflow-hidden ${heightClass} flex items-center mobile-overflow-hidden`}>
      {/* Animated Background - Hidden on mobile for performance */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-500/20 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent"></div>
        {/* Grid pattern - Hidden on mobile */}
        {!isMobile && (
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        )}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Hero Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6 sm:mb-8">
              <span className={`w-2 h-2 bg-green-400 rounded-full mr-2 ${animateClasses}`}></span>
              <span className="text-xs sm:text-sm font-medium text-white/90">
                {displayStats.active_jobs}+ Active Opportunities
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-6">
              Build Your
              <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-primary-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Academic Career
              </span>
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl text-slate-300 mb-8 sm:mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed mobile-px">
              Discover faculty positions, academic officer roles, and technology opportunities 
              at leading educational institutions and organizations.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mb-8 sm:mb-12 mobile-px">
              <Link
                to="/jobs"
                className={`touch-target group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-slate-900 bg-white rounded-xl hover:bg-slate-100 ${hoverScaleClasses} ${transitionClasses} shadow-lg shadow-white/25`}
              >
                <svg className={`w-5 h-5 mr-2 ${prefersReducedMotion ? '' : 'group-hover:scale-110 transition-transform'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Explore Jobs
              </Link>
              
              {!isAuthenticated ? (
                <Link
                  to="/login"
                  className={`touch-target group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 ${hoverScaleClasses} ${transitionClasses}`}
                >
                  <svg className={`w-5 h-5 mr-2 ${prefersReducedMotion ? '' : 'group-hover:scale-110 transition-transform'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Get Started
                </Link>
              ) : (
                <Link
                  to="/dashboard"
                  className={`touch-target group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 ${hoverScaleClasses} ${transitionClasses}`}
                >
                  <svg className={`w-5 h-5 mr-2 ${prefersReducedMotion ? '' : 'group-hover:scale-110 transition-transform'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  Dashboard
                </Link>
              )}
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6 text-slate-400">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-primary-400 to-blue-500 border-2 border-slate-900 flex items-center justify-center text-xs font-bold text-white">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <span className="text-xs sm:text-sm">{displayStats.total_candidates}+ Candidates</span>
              </div>
              <div className="h-4 w-px bg-slate-700 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-xs sm:text-sm">4.9/5 Rating</span>
              </div>
            </div>
          </div>

          {/* Hero Visual - Hidden on mobile for cleaner layout */}
          <div className="relative hidden lg:block">
            {/* Floating Cards */}
            <div className="relative">
              {/* Main Card */}
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 shadow-2xl">
                <div className="space-y-4">
                  {/* Job Card Preview 1 - Faculty */}
                  <div className={`bg-white rounded-xl p-4 shadow-lg ${prefersReducedMotion ? '' : 'transform hover:-translate-y-1 transition-transform duration-300'}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                        🎓
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">STEM Faculty</h4>
                        <p className="text-sm text-slate-500">JEE • NEET • On-campus</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Full-time</span>
                          <span className="text-xs text-slate-400">Posted 2h ago</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Job Card Preview 2 - Academic Officer */}
                  <div className={`bg-white rounded-xl p-4 shadow-lg ${prefersReducedMotion ? '' : 'transform hover:-translate-y-1 transition-transform duration-300'}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                        📋
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">Academic Officer</h4>
                        <p className="text-sm text-slate-500">Administration • Hybrid</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Full-time</span>
                          <span className="text-xs text-slate-400">Posted 5h ago</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Job Card Preview 3 - Software */}
                  <div className={`bg-white rounded-xl p-4 shadow-lg ${prefersReducedMotion ? '' : 'transform hover:-translate-y-1 transition-transform duration-300'}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center text-white font-bold">
                        💻
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">Software Developer</h4>
                        <p className="text-sm text-slate-500">EdTech • Remote</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Full-time</span>
                          <span className="text-xs text-slate-400">Posted 1d ago</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className={`absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-primary-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg ${bounceClasses}`}>
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <div className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-xl rounded-xl p-4 border border-white/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-white font-semibold">{displayStats.total_placements}+</div>
                    <div className="text-slate-400 text-sm">Placements</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
